import { ENV, GIT_SHA } from 'packages/env';
import { IssuerName } from 'packages/lib/auth/data';
import { loadConfig } from 'packages/lib/config';
import { logError } from 'packages/lib/utils/logger';
import { ServiceLevelIndicatorMetadata } from 'packages/lib/metrics/types';

// Aug-20-2026 Used Cursor (auto) to generate postAuthEvent and helpers

/**
 * Login / logout events. Sent immediately (not via the debounced tech-metrics queue)
 * so logout is not lost when the page navigates to Okta.
 */
type AuthEventName = 'login' | 'logout';

interface AuthMetric {
  event_name: AuthEventName;
  timestamp: string;
  metadata: ServiceLevelIndicatorMetadata;
}

export interface AuthEventIdentity {
  email: string;
  tenant: IssuerName;
}

function groupFromTenant(tenant: IssuerName): string {
  if (tenant === 'contractor') {
    return 'imerit';
  }
  if (tenant === 'primary') {
    return 'motional';
  }
  return 'unknown';
}

export function buildAuthEventBody(
  eventName: AuthEventName,
  identity: AuthEventIdentity,
  occurredAt: Date = new Date()
): AuthMetric {
  return {
    event_name: eventName,
    timestamp: occurredAt.toISOString(),
    metadata: {
      env: ENV,
      version: GIT_SHA,
      url: window.location.href,
      clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      email: identity.email,
      group: groupFromTenant(identity.tenant),
      tags: [],
    },
  };
}

/**
 * Send a login or logout metric immediately. Logout cannot use the debounced
 * tech-metrics queue because navigation to Okta would drop the event.
 *
 * Posts with fetch + an access token from the caller so this module does not
 * import packages/lib/auth (which would cycle with okta.ts).
 */
export async function postAuthEvent(
  eventName: AuthEventName,
  identity: AuthEventIdentity,
  accessToken?: string
): Promise<void> {
  const event = buildAuthEventBody(eventName, identity);

  if (ENV === 'local') {
    console.info('Auth metric (not sent in local env):', event);
    return;
  }

  try {
    const { techMetricsHost } = loadConfig().apiHosts;
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    };
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
    const resp = await fetch(`${techMetricsHost}/metrics`, {
      method: 'POST',
      headers,
      body: JSON.stringify([event]),
    });
    if (!resp.ok) {
      logError(
        `Failed to send auth metric (${resp.status}): ${resp.statusText}`
      );
    }
  } catch (err) {
    logError(`Failed to send auth metric: ${err}`);
  }
}
